<template>
    <div>
        <jet-form-section :in-modal="inModal" @submitted="submitForm">
            <template #title v-if="!inModal">Payout Method</template>
            <template #description v-if="!inModal">{{ description }}</template>
            <template #form>
                <div v-if="inModal" class="col-span-6">
                    <div class="text-lg font-medium text-gray-900">Payout Method</div>
                    <div class="mt-3">{{ description }}</div>
                </div>

                <!-- Payout Method Selection -->
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="payoutMethod" value="Choose Your Payout Method" />
                    <select id="payoutMethod" v-model="form.payoutMethod" class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                        <!--<option value="bankAccount">Direct Deposit</option>-->
                        <option value="cashApp">Cash App</option>
                        <option value="venmo">Venmo</option>
                    </select>
                    <!--<div v-if="form.payoutMethod === 'bankAccount' && plaidAccountsNotFound" class="mt-2">-->
                    <!--    <a href="#" @click.prevent="openPlaidLink" class="text-blue-700 hover:text-blue-900">Connect to your bank account using Plaid <span class="text-xl">&rsaquo;</span></a>-->
                    <!--</div>-->
                    <jet-input-error :message="form.errors.payoutMethod" class="mt-2" />
                </div>

                <!-- Cash App Specific Fields -->
                <template v-if="form.payoutMethod === 'cashApp'">
                    <div class="col-span-6 sm:col-span-4">
                        <jet-label for="cashApp" value="Cash App" />
                        <jet-input id="cashApp" v-model="form.cashApp" type="text" class="mt-1 block w-full" placeholder="$Cashtag" autocomplete="off" />
                        <jet-input-error :message="form.errors.cashApp" class="mt-2" />
                    </div>
                </template>

                <!-- Venmo Specific Fields -->
                <template v-else-if="form.payoutMethod === 'venmo'">
                    <div class="col-span-6 sm:col-span-4">
                        <jet-label for="venmo" value="Venmo" />
                        <jet-input id="venmo" v-model="form.venmo" type="text" class="mt-1 block w-full" placeholder="@Username" autocomplete="off" />
                        <jet-input-error :message="form.errors.venmo" class="mt-2" />
                    </div>
                </template>

                <template v-if="form.payoutMethod === 'bankAccount' && plaidAccounts">
                    <div v-if="Object.keys(groupedAccounts).length > 0" class="col-span-6 sm:col-span-4 text-sm font-medium">Choose which account will receive deposits.</div>
                    <div class="col-span-6 sm:col-span-4" v-for="(accounts, institutionName) in groupedAccounts" :key="institutionName">
                        <div class="mb-2 ml-4 font-bold">
                            <div>{{ institutionName }}</div>
                        </div>
                        <div v-for="account in accounts" :key="account.id" class="ml-0 sm:ml-8 mb-3 p-5 flex justify-between rounded-lg transition" :class="account.default ? 'border-4 border-blue-700' : 'opacity-60 hover:opacity-100 border-2 hover:border-gray-500 border-gray-300'">
                            <div>
                                <div class="text-sm font-medium">Account Name</div>
                                <div class="text-ellipsis overflow-hidden">{{ account.account_name }}</div>
                                <div v-if="!account.default" class="flex gap-4">
                                    <a @click.prevent="setAccountDefault(account.id)" href="#" class="text-sm font-medium text-blue-700 mt-2" title="Make Default">Make Default</a>
                                    <a @click.prevent="confirmRemove(account.id)" href="#" class="text-sm font-medium text-red-700 mt-2" title="Remove Account">Remove</a>
                                </div>
                                <div v-if="account.default" class="flex gap-4">
                                    <div v-if="accounts.length > 1" @click="setAccountDefault(account.id)" class="text-sm text-blue-900 font-medium mt-2" title="Default Account">Default</div>
                                    <a v-if="accounts.length < 2" @click.prevent="confirmRemove(account.id)" href="#" class="text-sm font-medium text-red-700 mt-2" title="Remove Account">Remove</a>
                                </div>
                            </div>
                            <div>
                                <div class="text-sm font-medium">Account Number</div>
                                <div>{{ account.mask }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </template>

            <template #actions>
                <div v-if="form.payoutMethod !== 'bankAccount'">
                    <jet-button class="ml-2" :disabled="form.processing" :class="{ 'opacity-25': form.processing }">Save</jet-button>
                </div>
                <div v-if="form.payoutMethod === 'bankAccount'">
                    <jet-input-error :message="form.errors.bankAccount" class="mr-3" />
                    <plaid ref="plaidComponent" :accounts="plaidAccounts" @accounts-stored="fetchPlaidAccounts" @close="close" @open="open"></plaid>
                </div>
            </template>
        </jet-form-section>

        <jet-dialog-modal :show="confirmingRemove" @close="confirmingRemove = false">
            <template #title>Remove Bank Account</template>
            <template #content> Are you sure you want to remove this bank account? This action cannot be undone. </template>
            <template #footer>
                <jet-secondary-button @click.native="confirmingRemove = false">Cancel</jet-secondary-button>
                <jet-button class="ml-2" @click.native="removePlaidAccount" :disabled="form.processing"> Remove </jet-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
export default {
    mounted() {
        if (this.form.payoutMethod === "bankAccount") {
            this.fetchPlaidAccounts()
        }
    },

    components: {
        JetFormSection: require("@/Jetstream/FormSection").default,
        JetButton: require("@/Jetstream/Button").default,
        JetSecondaryButton: require("@/Jetstream/SecondaryButton").default,
        JetActionMessage: require("@/Jetstream/ActionMessage").default,
        JetInput: require("@/Jetstream/Input").default,
        JetLabel: require("@/Jetstream/Label").default,
        JetInputError: require("@/Jetstream/InputError").default,
        JetDialogModal: require("@/Jetstream/DialogModal").default,
        Plaid: require("@/Pages/Plaid/Plaid").default,
    },
    props: {
        inModal: {
            default: false,
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: "POST",
                payoutMethod: this.$page.props.user.payoutMethod,
                cashApp: this.$page.props.user.cashApp,
                venmo: this.$page.props.user.venmo,
            }),
            defaultAccountForm: this.$inertia.form({
                _method: "POST",
                id: null,
            }),
            description: "Choose your preferred payout method to receive your earnings.",
            recentlySaved: false,
            plaidAccounts: [],
            plaidAccountsNotFound: false,
            confirmingRemove: false,
            selectedAccountId: null,
        }
    },
    computed: {
        isValidCashAppTag() {
            // Cash App: Only letters and numbers are allowed after the $
            return /^\$[a-zA-Z0-9]+$/.test(this.form.cashApp)
        },
        isValidVenmoUsername() {
            // Venmo: Allows letters, numbers, hyphens, and underscores
            return /^@[a-zA-Z0-9_-]+$/.test(this.form.venmo)
        },
        groupedAccounts() {
            return this.plaidAccounts.reduce((acc, account) => {
                ;(acc[account.institution_name] = acc[account.institution_name] || []).push(account)
                return acc
            }, {})
        },
    },
    watch: {
        "form.recentlySuccessful"(newValue) {
            if (newValue) {
                this.recentlySaved = true
            }
        },
        "form.payoutMethod"(newVal) {
            if (newVal === "bankAccount") {
                this.fetchPlaidAccounts()
            } else {
                this.plaidAccounts = [] // Reset or clear Plaid accounts when another method is selected
            }
        },
    },
    methods: {
        submitForm() {
            this.form.clearErrors()
            if (this.form.payoutMethod === "cashApp" && !this.isValidCashAppTag) {
                this.form.errors.cashApp = "A valid $Cashtag must start with a '$' followed by letters and numbers only."
                return
            }
            if (this.form.payoutMethod === "venmo" && !this.isValidVenmoUsername) {
                this.form.errors.venmo = "A valid @Username must start with '@' and can include letters, numbers, hyphens, and underscores."
                return
            }
            this.form.processing = true
            this.form
                .transform((data) => ({
                    ...data,
                    cashApp: data.cashApp,
                    venmo: data.venmo,
                    payoutMethod: data.payoutMethod,
                }))
                .post(route("user.payout.update"), {
                    errorBag: "updatePayout",
                    preserveScroll: true,
                    onFinish: () => this.close(),
                })
        },
        setAccountDefault(id) {
            axios
                .post(route("plaid.accounts.default"), { id: id })
                .then((response) => {
                    this.fetchPlaidAccounts()
                })
                .catch((error) => {
                    console.error("Error updating default account:", error)
                    // Handle error, potentially update errorBag here
                })
        },
        fetchPlaidAccounts() {
            fetch(route("plaid.accounts"))
                .then((response) => response.json())
                .then((data) => {
                    // Check if the response is the specific "not found" message
                    if (data.error === "Plaid account not found") {
                        this.plaidAccountsNotFound = true
                        this.plaidAccounts = [] // Ensure plaidAccounts is an empty array
                    } else {
                        this.plaidAccounts = Array.isArray(data) ? data : []
                        this.plaidAccountsNotFound = false // Reset the flag
                    }
                })
                .catch((error) => {
                    console.error("Error fetching Plaid accounts info:", error)
                    this.plaidAccounts = [] // Reset to an empty array on error
                    this.plaidAccountsNotFound = false
                })
        },
        deletePlaidAccount(accountId) {
            this.$inertia.post(
                route("plaid.accounts.delete"),
                { account_id: accountId },
                {
                    onSuccess: () => {
                        // Filter out the deleted account from the plaidAccounts array
                        this.plaidAccounts = this.plaidAccounts.filter((account) => account.id !== accountId)
                        // Notify Vue about the update to re-render the component
                        this.$forceUpdate()
                    },
                    onError: (error) => {
                        console.error("Failed to delete the account:", error)
                    },
                    preserveScroll: true,
                    preserveState: true,
                }
            )
        },
        confirmRemove(accountId) {
            this.selectedAccountId = accountId
            this.confirmingRemove = true
        },
        removePlaidAccount() {
            console.log("Remove plaid account")
            if (this.selectedAccountId) {
                this.deletePlaidAccount(this.selectedAccountId)
                // Logic to close the modal and reset the selected account ID
                this.confirmingRemove = false
                this.selectedAccountId = null
            }
        },
        openPlaidLink() {
            if (this.$refs.plaidComponent) {
                this.$refs.plaidComponent.createLinkToken()
            }
        },
        close() {
            this.$emit("close")
        },
        open() {
            this.$emit("open")
        },
    },
}
</script>
